import { StudentRoomManager } from "@/manager/room/student.manager";
import { ClassRoomModel, RoomModel } from "@/models";
import { GLApiStatus, GLError } from "@/models/error.model";
import { UserModel } from "@/models/user.model";
import { GetterTree } from "vuex";
import { HelperState, StudentState, TeacherState } from "../interface";
import { ClassAction, StudentRoomState } from "./state";
import { defineVuexName, MODULES } from "@/store/utils";

const getters: GetterTree<StudentRoomState, any> = {
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_INIT_CLASS_LOADING)](state: StudentRoomState): boolean {
    return state.initClassLoading;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_SOCKET_CONNECTED)](state: StudentRoomState): boolean {
    return state.isConnected;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_AGORA_CLIENT_INITIALIZED)](state: StudentRoomState): boolean {
    return !!state.manager?.agoraClient?.initialized;
  },
  error(state: StudentRoomState): GLError | null {
    return state.error;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_API_STATUS)](state: StudentRoomState): GLApiStatus | null {
    return state.apiStatus;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_INFO)](state: StudentRoomState): RoomModel {
    return state.info as RoomModel;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_CLASS_INFO)](state: StudentRoomState): ClassRoomModel | undefined {
    return state.info?.classInfo;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_STUDENTS)](state: StudentRoomState): Array<StudentState> {
    return state.students;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_TEACHER)](state: StudentRoomState): TeacherState {
    return state.teacher as TeacherState;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_STUDENT)](state: StudentRoomState): StudentState {
    return state.student as StudentState;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_LOCAL_STUDENT_ID)](state: StudentRoomState): string | undefined {
    return state.student?.id;
  },
  roomManager(state: StudentRoomState): StudentRoomManager {
    return state.manager as StudentRoomManager;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_CLASS_ACTION)](state: StudentRoomState): ClassAction {
    return state.classAction;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)](state: StudentRoomState): string {
    return state.idOne;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_STUDENT_MODE_HELPER_ONE_ID)](state: StudentRoomState): string {
    return state.idHelperOne;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_ONE_TO_ONE_STUDENT_IDS)](state: StudentRoomState): string[] {
    const studentIds = [];
    if (state.idOne) {
      studentIds.push(state.idOne);
    }
    if (state.idHelperOne) {
      studentIds.push(state.idHelperOne);
    }
    return studentIds;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_SPEAKING_USERS)](state: StudentRoomState): Array<string> {
    return state.speakingUsers;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_SHOW_WHITEBOARD)](state: StudentRoomState): boolean {
    return state.showWhiteBoard;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_DISCONNECTED)](state: StudentRoomState): boolean {
    return state.isDisconnected;
  },
  isJoined(state: StudentRoomState): boolean {
    return state.isJoined;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_TEACHER_IS_DISCONNECTED)](state: StudentRoomState): boolean {
    return state.teacherIsDisconnected;
  },
  getAvatarStudentOneToOne(state: StudentRoomState): string {
    return state.avatarStudentOneToOne;
  },
  getAvatarStudentHelperOneToOne(state: StudentRoomState): string {
    return state.avatarStudentHelperOneToOne;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_VIDEOS_FEED_VISIBLE)](state: StudentRoomState): boolean {
    return state.videosFeedVisible;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_USER)](state: StudentRoomState): UserModel | undefined {
    return state.user;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_LOGGED_IN_AS_STUDENT)](state: StudentRoomState): boolean {
    return !!state.user?.id;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_MEDIA_STATE)](state: StudentRoomState): boolean {
    return state.mediaState;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_HELPER_INFO)](state: StudentRoomState): HelperState | undefined {
    return state.helper;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_HELPER_ID)](state: StudentRoomState): string | undefined {
    return state.helper?.id;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_HELPER_VIDEO_STATUS)](state: StudentRoomState): boolean {
    return state.helper?.isVideoShownByTeacher ?? false;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_HELPER_AVATAR)](state: StudentRoomState): string {
    return state.helper?.avatar ?? "";
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_ME_IN_ONE_ONE)](state: StudentRoomState): boolean {
    return state.idOne === state.student?.id || state.idHelperOne === state.student?.id;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_IN_ONE_TO_ONE_MODE_WITH_HELPER)](state: StudentRoomState): boolean {
    return state.idHelperOne === state.student?.id;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_IN_ONE_TO_ONE_MODE_WITH_TEACHER)](state: StudentRoomState): boolean {
    return !!(state.idOne && state.idOne === state.student?.id);
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_OTHER_STUDENT_IN_ONE_TO_ONE_WITH_TEACHER)](state: StudentRoomState): boolean {
    return !!(state.idOne && state.idOne !== state.student?.id);
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_OTHER_STUDENT_IN_ONE_TO_ONE_WITH_HELPER)](state: StudentRoomState): boolean {
    return !!(state.idHelperOne && state.idHelperOne !== state.student?.id);
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_LOCAL_STUDENT_RAISED_HAND)](state: StudentRoomState): boolean {
    return !!state.student?.raisingHand;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_ONE_TO_ONE_STUDENT_ID)](s: StudentRoomState) {
    return s.idOne;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_HELPER_ONE_TO_ONE_STUDENT_ID)](s: StudentRoomState) {
    return s.idHelperOne;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_TEACHER_ALLOW_STUDENT_SEE_HELPER_VIDEO)](s: StudentRoomState): boolean {
    return s.helper?.isVideoShownByTeacher ?? false;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_IS_SHOW_CLASSMATE_VIDEOS)](s: StudentRoomState): boolean {
    return s.videosFeedVisible;
  },
  [defineVuexName(MODULES.STUDENT_ROOM.GETTERS.GET_ROOM_MANAGER)](state: StudentRoomState): StudentRoomManager {
    return state.manager as StudentRoomManager;
  },
};

export default getters;
